import React, { useEffect, useState } from 'react';
import {
  Globe, Zap, Droplets, Flame, Bot, Inbox, ScanText, ShieldCheck, CheckCircle2, CreditCard, BarChart3, FileBarChart,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const PORTALS = [
  { name: 'City Electric', icon: Zap },
  { name: 'Metro Water', icon: Droplets },
  { name: 'Regional Gas', icon: Flame },
];

const STAGES = [
  { label: 'Collect', icon: Inbox },
  { label: 'Extract', icon: ScanText },
  { label: 'Validate', icon: ShieldCheck },
  { label: 'Approve', icon: CheckCircle2 },
  { label: 'Pay', icon: CreditCard },
  { label: 'Report', icon: FileBarChart },
];

function Connector({ dots = 2 }) {
  return (
    <div className="relative mx-auto my-2 h-6 w-px bg-slate-200 dark:bg-white/10">
      {Array.from({ length: dots }).map((_, i) => (
        <span
          key={i}
          className="bp-drop absolute left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-brand shadow-[0_0_8px_rgba(255,87,87,0.9)]"
          style={{ animationDelay: `${i * 0.8}s` }}
        />
      ))}
    </div>
  );
}

export default function HeroArchitecture({ className }) {
  const [active, setActive] = useState(0);
  const [bills, setBills] = useState([3, 5, 4]);
  const [bars, setBars] = useState([42, 60, 48, 72, 55, 84, 66, 92, 74]);

  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduced) { setActive(2); return undefined; }
    const id = setInterval(() => setActive((s) => (s + 1) % STAGES.length), 900);
    return () => clearInterval(id);
  }, [reduced]);

  // Bill counts drift randomly per portal
  useEffect(() => {
    if (reduced) return undefined;
    const id = setInterval(() => {
      setBills((prev) => prev.map((v) => {
        const next = v + (Math.random() < 0.5 ? -1 : 1) * (1 + Math.floor(Math.random() * 3));
        return Math.min(48, Math.max(1, next));
      }));
    }, 1500);
    return () => clearInterval(id);
  }, [reduced]);

  // Chart bars gently rise / fall
  useEffect(() => {
    if (reduced) return undefined;
    const id = setInterval(() => {
      setBars((prev) => prev.map((h) => {
        const next = h + (Math.random() < 0.5 ? -1 : 1) * (2 + Math.floor(Math.random() * 8));
        return Math.min(98, Math.max(28, next));
      }));
    }, 1200);
    return () => clearInterval(id);
  }, [reduced]);

  return (
    <div
      data-testid="hero-flow"
      className={cn(
        'relative overflow-hidden rounded-2xl border p-4 shadow-xl sm:p-5',
        'border-slate-200 bg-white shadow-slate-300/40',
        'dark:border-white/10 dark:bg-gradient-to-b dark:from-[#0e0e16] dark:to-[#08080d] dark:shadow-black/60',
        className,
      )}
    >
      <div className="pointer-events-none absolute -right-10 top-8 h-40 w-40 rounded-full bg-brand/10 blur-3xl dark:bg-brand/15" />
      <div className="pointer-events-none absolute -left-10 bottom-10 h-36 w-36 rounded-full bg-[#ff8a5b]/8 blur-3xl dark:bg-[#ff8a5b]/10" />

      {/* SOURCE — browser */}
      <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="flex items-center gap-1.5 border-b border-slate-200 bg-slate-100 px-3 py-1.5 dark:border-white/10 dark:bg-white/[0.04]">
          <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
          <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
          <span className="h-2 w-2 rounded-full bg-[#28c840]" />
          <div className="mx-auto flex items-center gap-1 rounded bg-white px-2 py-0.5 text-[10px] text-slate-500 ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-400 dark:ring-white/10">
            <Globe className="h-3 w-3 text-brand" /> billportal.io
          </div>
          <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10" data-testid="hero-live-clock">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none">
              <circle cx="12" cy="12" r="9" strokeWidth="2" className="stroke-slate-300 dark:stroke-white/25" />
              <line x1="12" y1="12" x2="12" y2="7.5" strokeWidth="2" strokeLinecap="round" className="stroke-brand animate-[spin_8s_linear_infinite] [transform-origin:12px_12px]" />
              <line x1="12" y1="12" x2="12" y2="6" strokeWidth="1.5" strokeLinecap="round" className="stroke-brand/60 animate-[spin_2s_linear_infinite] [transform-origin:12px_12px]" />
            </svg>
            24/7
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 p-2.5">
          {PORTALS.map((p, i) => (
            <div key={p.name} className="rounded-md border border-slate-200 bg-white p-2 text-center dark:border-white/10 dark:bg-white/[0.02]">
              <span className="mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-md bg-brand/15 text-brand"><p.icon className="h-3.5 w-3.5" /></span>
              <div className="text-[11px] font-bold tabular-nums text-slate-900 transition-all duration-300 dark:text-white" data-testid={`hero-bill-count-${i}`}>{bills[i]} bills</div>
              <div className="text-[9px] text-slate-500">{p.name.split(' ')[0]}</div>
            </div>
          ))}
        </div>
      </div>

      <Connector />

      {/* ORCHESTRATION — AI agents + humans */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-3.5 py-1.5">
          <Bot className="h-4 w-4 text-brand" />
          <span className="text-xs font-semibold text-slate-900 dark:text-white">AI Agents</span>
          <span className="text-[10px] text-slate-500 dark:text-slate-400">+ humans in the loop</span>
        </div>
      </div>

      <Connector />

      {/* PIPELINE — stages */}
      <div className="relative px-1">
        <div className="absolute left-5 right-5 top-4 h-px bg-slate-200 dark:bg-white/10" />
        <span className="bp-slide-x absolute top-4 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-brand shadow-[0_0_8px_rgba(255,87,87,0.9)]" />
        <div className="relative flex items-start justify-between">
          {STAGES.map((s, i) => {
            const on = i === active;
            const Ic = s.icon;
            return (
              <div key={s.label} className="flex flex-col items-center gap-1.5">
                <span className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-300',
                  on
                    ? 'scale-110 border-brand bg-brand text-white shadow-lg shadow-brand/50'
                    : 'border-slate-200 bg-white text-slate-400 dark:border-white/10 dark:bg-[#0e0e16] dark:text-slate-500',
                )}>
                  <Ic className="h-3.5 w-3.5" />
                </span>
                <span className={cn('text-[9px] font-medium transition-colors', on ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500')}>{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <Connector />

      {/* OUTPUT — insights */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-900 dark:text-white"><BarChart3 className="h-3.5 w-3.5 text-brand" /> Insights</div>
          <span className="text-[9px] text-slate-400 dark:text-slate-500">Financial · Operational · Sustainability</span>
        </div>
        <div className="flex h-11 items-end gap-1">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 rounded-t-sm bg-gradient-to-t from-brand/30 to-brand transition-all duration-700 ease-out" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-center gap-1.5 text-center text-[10px] text-slate-500">
        <ShieldCheck className="h-3 w-3 shrink-0 text-brand" /> Every action, decision &amp; conversation — complete audit trail
      </div>
    </div>
  );
}
